import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";

import api from "/imports/api/client";

const BOOKINGS_TAB = "dashboard?tab=bookings&scope=upcoming";

// The club changed the lesson while the client was moving it: nothing was moved.
const isBookingChanged = (err) => err?.data?.data?.reason === "BOOKING_CHANGED";

/**
 * Reschedule mode of the lessons page (`?reschedule=<bookingId>`): the page lists the
 * times this paid lesson can move to, and a picked time opens a confirm step instead of
 * the cart. `info` is the server's description of the move (mode, credit, pack, allowed
 * venues, the lesson being moved), refreshed with every availability load.
 */
export default function useLessonReschedule({ slug, bookingId, onStale }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [info, setInfo] = useState(null);
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const active = !!bookingId;
  const backToBookings = () => navigate(`/${slug}/${BOOKINGS_TAB}`);

  const select = (slot, date) => setTarget({ slot, date });
  const close = () => {
    if (!submitting) setTarget(null);
  };

  // The club changed or cancelled the booking while this page was open: there is nothing
  // left to move, so back to the bookings list, as the confirm step does.
  const leaveIfChanged = (err) => {
    if (!isBookingChanged(err)) return false;
    enqueueSnackbar(err.message, { variant: "warning" });
    backToBookings();
    return true;
  };

  const confirm = async () => {
    if (!target) return;
    const { slot, date } = target;
    setSubmitting(true);
    try {
      // One of the client's own booked lessons: the pack pays for that booking, so the
      // move names it rather than a time to create.
      const body = slot.ownBookingId
        ? { targetBookingId: slot.ownBookingId }
        : {
            slot: {
              date,
              time: slot.time,
              trainerId: slot.trainerId,
              facilityId: slot.facilityId || null,
              // The court under a lesson is always picked by the club's court picker.
              facilityAuto: !!slot.facilityId,
              locationId: slot.locationId
            }
          };
      const result = await api.rescheduleBooking(slug, bookingId, body);
      const unpaidCopy =
        result.mode === "MONEY_MOVE"
          ? "Your lesson is moved and your payment is in your wallet, but we couldn't apply it — please use Pay Now with your wallet on the new booking."
          : "Your lesson is moved, but we couldn't take the package session — please use Pay Now on the new booking.";
      const movedCopy = slot.ownBookingId
        ? `Your package now pays for your lesson on ${DateTime.fromISO(date).toFormat("ccc d LLL")} at ${slot.time}.`
        : `Lesson moved to ${DateTime.fromISO(date).toFormat("ccc d LLL")} at ${slot.time}.`;
      enqueueSnackbar(result.requiresPayment ? unpaidCopy : movedCopy, { variant: result.requiresPayment ? "warning" : "success" });
      backToBookings();
    } catch (err) {
      setSubmitting(false);
      if (isBookingChanged(err)) {
        enqueueSnackbar(err.message, { variant: "warning" });
        backToBookings();
        return;
      }
      enqueueSnackbar(err.message || "Couldn't move your lesson", { variant: "error" });
      // The time was taken or stopped qualifying while the client decided: show the day as it is now.
      setTarget(null);
      onStale();
    }
  };

  return { active, bookingId, info, setInfo, target, select, close, confirm, submitting, cancel: backToBookings, leaveIfChanged };
}
